import { LangageIso6391 } from "@tfg/db"
import type { TextContent, TextTranslation } from "@tfg/db"
import { CountryFlag } from "@tfg/ui"
import { FlagComponent } from "country-flag-icons/react/1x1"
import { ReactElement } from "react"
import { useTranslation } from "react-i18next"

import { Locale } from "@tfg/types/schemas/enum"

import { i18n } from "~/i18n"

export type LanguageInfo = {
  languageName: string
  originalLanguageName: string
  flag: ReactElement<FlagComponent>
}
export function useLocalizedTextByTextContent() {
  const { i18n } = useTranslation()

  return (
    textContent: TextContent & {
      TextTranslation: TextTranslation[]
    }
  ) => {
    const currentLanguage = i18n.language

    const translation = textContent.TextTranslation.find(
      (translation) =>
        translation.translatedLanguage === currentLanguage.toUpperCase()
    )

    return translation ? translation.translation : textContent.originalText
  }
}
export const LANGUAGES: {
  [language in LangageIso6391]: LanguageInfo
} = {
  FR: {
    languageName: i18n.t("languages.french"),
    originalLanguageName: "Français",
    flag: <CountryFlag countryIso3166="FR" />,
  },
  EN: {
    languageName: i18n.t("languages.english"),
    originalLanguageName: "English",
    flag: <CountryFlag countryIso3166="GB" />,
  },
  ZH: {
    languageName: i18n.t("languages.chinese"),
    originalLanguageName: "中文",
    flag: <CountryFlag countryIso3166="CN" />,
  },
  PT: {
    languageName: i18n.t("languages.portuguese"),
    originalLanguageName: "Português",
    flag: <CountryFlag countryIso3166="PT" />,
  },
  IT: {
    languageName: i18n.t("languages.italian"),
    originalLanguageName: "Italiano",
    flag: <CountryFlag countryIso3166="IT" />,
  },
  ES: {
    languageName: i18n.t("languages.spanish"),
    originalLanguageName: "Español",
    flag: <CountryFlag countryIso3166="ES" />,
  },
  TR: {
    languageName: i18n.t("languages.turkish"),
    originalLanguageName: "Türkçe",
    flag: <CountryFlag countryIso3166="TR" />,
  },
  NL: {
    languageName: i18n.t("languages.dutch"),
    originalLanguageName: "Nederlands",
    flag: <CountryFlag countryIso3166="NL" />,
  },
  DE: {
    languageName: i18n.t("languages.german"),
    originalLanguageName: "Deutsch",
    flag: <CountryFlag countryIso3166="DE" />,
  },
} as const

export const findLanguage = (locale: string): string => {
  const language = LANGUAGES[locale as keyof typeof LANGUAGES] || LANGUAGES.EN
  return language.originalLanguageName
}

export const findLanguageFlag = (
  locale: Locale
): ReactElement<FlagComponent> => {
  const language = LANGUAGES[locale as keyof typeof LANGUAGES] || LANGUAGES.EN

  return language.flag
}
